import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * This hook is used to manage the state of a modal.
 * It is used to open and close the modal.
 * It is also used to handle the hash of the URL.
 *
 * @param hashProp - The hash of the URL.
 * @param onOpen - Called when the modal opens, including via deep link or browser navigation.
 * @returns The modal state.
 */
const useModalState = (hashProp: string, onOpen?: () => void) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { hash } = useLocation();

  const openModal = useCallback(() => {
    if (hashProp) {
      navigate(`#${hashProp}`);
    }
  }, [navigate, hashProp]);

  const closeModal = useCallback(() => {
    navigate('#');
  }, [navigate]);

  useEffect(() => {
    if (hashProp) {
      const isOpen = hash === `#${hashProp}`;
      setIsModalOpen(isOpen);

      if (isOpen) {
        onOpen?.();
      }
    }
  }, [hash, hashProp, onOpen]);

  return { isModalOpen, setIsModalOpen, openModal, closeModal };
};

export default useModalState;
